import {
  isNotificationCategoryOptional,
  type NotificationRenderContext,
  notificationCategoryLabel,
  notificationTypeDefinition,
  renderNotification,
  type StorefrontTheme,
} from "@loot-os/api-contract";
import { Badge } from "@loot-os/ui/components/badge";
import { Button } from "@loot-os/ui/components/button";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@loot-os/ui/components/card";
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemGroup,
  ItemTitle,
} from "@loot-os/ui/components/item";
import { NotificationIconTile } from "@loot-os/ui/components/notification-item";
import { Skeleton } from "@loot-os/ui/components/skeleton";
import { toast } from "@loot-os/ui/components/sonner";
import { Switch } from "@loot-os/ui/components/switch";
import { useState } from "react";

import { ErrorAlert } from "../components/ErrorAlert";
import { NotificationPreview } from "../components/NotificationPreview";
import { NotificationsTabs } from "../components/NotificationsTabs";
import { NotificationTemplateSheet } from "../components/NotificationTemplateSheet";
import { StorefrontSection } from "../components/StorefrontSection";
import {
  draftFromContent,
  isDefaultOverride,
  type NotificationTemplateRow,
  SYSTEM_NOTIFICATION_CATEGORIES,
  templateOverride,
  unavailableNote,
  WELCOME_NOTIFICATION_TYPE,
} from "../lib/notifications";
import { useNotificationPageData, useNotificationTemplateWrites } from "../lib/useNotifications";
import { useTenantRole } from "../lib/useTenantRole";

/**
 * The system notifications of one environment: the welcome message on its own card, then every
 * other type by category, each with its switch and an editor any member can open and an editor
 * can save.
 */
export function NotificationsSystemPage() {
  const { can } = useTenantRole();
  const canEdit = can("editor");
  const { templates, storefront } = useNotificationPageData();
  const { update, reset } = useNotificationTemplateWrites();
  // The type open in the editor; kept while the sheet closes, so it closes with its content.
  const [selected, setSelected] = useState<NotificationTemplateRow["type"] | null>(null);
  const [open, setOpen] = useState(false);
  const [now] = useState(() => new Date());

  const error = templates.error ?? storefront.error;
  if (error) return <ErrorAlert message={error.message} />;
  if (!templates.data || !storefront.data) return <Skeleton className="h-96" />;

  const { context } = templates.data;
  const { environment, assetsAvailable } = storefront.data;
  const { theme } = storefront.data.config;
  const rows = templates.data.templates;
  const welcome = rows.find((row) => row.type === WELCOME_NOTIFICATION_TYPE);
  const selectedRow = rows.find((row) => row.type === selected) ?? null;

  const edit = (row: NotificationTemplateRow) => {
    setSelected(row.type);
    setOpen(true);
  };

  // The row's switch saves at once: the template as it stands with the switch flipped.
  const setEnabled = (row: NotificationTemplateRow, enabled: boolean) => {
    const override = templateOverride(draftFromContent(row.template), enabled, row.defaults);
    const onSuccess = () => toast.success(`${row.label} turned ${enabled ? "on" : "off"}.`);
    if (isDefaultOverride(override)) reset.mutate({ type: row.type }, { onSuccess });
    else update.mutate({ type: row.type, override }, { onSuccess });
  };
  const pendingType = update.isPending
    ? update.variables?.type
    : reset.isPending
      ? reset.variables?.type
      : undefined;

  return (
    <StorefrontSection
      title="Notifications"
      description="What shoppers are told when something happens to their orders, balance or account. Each one can be reworded, given a banner or a button, or turned off. They read in this store's words and currency."
      environment={environment}
    >
      <NotificationsTabs active="system" />
      {welcome && (
        <WelcomeCard
          row={welcome}
          context={context}
          theme={theme}
          now={now}
          canEdit={canEdit}
          pending={pendingType === welcome.type}
          onEdit={() => edit(welcome)}
          onEnabledChange={(enabled) => setEnabled(welcome, enabled)}
        />
      )}

      {SYSTEM_NOTIFICATION_CATEGORIES.map((category) => {
        const inCategory = rows.filter(
          (row) => row.category === category && row.type !== WELCOME_NOTIFICATION_TYPE,
        );
        if (inCategory.length === 0) return null;
        return (
          <Card key={category}>
            <CardHeader>
              <CardTitle>{notificationCategoryLabel(category, context.terms)}</CardTitle>
              <CardDescription>
                {isNotificationCategoryOptional(category)
                  ? "Shoppers can turn these off in their notification settings."
                  : "Shoppers always get these: receipts and account notices can't be turned off on their side."}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ItemGroup className="gap-0 divide-y">
                {inCategory.map((row) => (
                  <TemplateRow
                    key={row.type}
                    row={row}
                    context={context}
                    canEdit={canEdit}
                    pending={pendingType === row.type}
                    onEdit={() => edit(row)}
                    onEnabledChange={(enabled) => setEnabled(row, enabled)}
                  />
                ))}
              </ItemGroup>
            </CardContent>
          </Card>
        );
      })}

      <NotificationTemplateSheet
        row={selectedRow}
        open={open}
        onOpenChange={setOpen}
        context={context}
        theme={theme}
        uploadsAvailable={assetsAvailable}
        canEdit={canEdit}
      />
    </StorefrontSection>
  );
}

interface TemplateRowProps {
  row: NotificationTemplateRow;
  context: NotificationRenderContext;
  canEdit: boolean;
  /** A switch flip of this row is saving. */
  pending: boolean;
  onEdit: () => void;
  onEnabledChange: (enabled: boolean) => void;
}

/** The badges a type wears: edited, off, or not sent here. */
function TemplateBadges({ row }: { row: NotificationTemplateRow }) {
  return (
    <>
      {row.customized && <Badge variant="secondary">Customized</Badge>}
      {!row.template.enabled && <Badge variant="outline">Off</Badge>}
    </>
  );
}

/** One system type: its icon, what it is and when it fires, its switch and its editor. */
function TemplateRow({
  row,
  context,
  canEdit,
  pending,
  onEdit,
  onEnabledChange,
}: TemplateRowProps) {
  return (
    <Item size="sm" role="listitem" className="rounded-none px-0 py-3">
      <NotificationIconTile category={row.category} icon={row.template.icon} />
      <ItemContent className="min-w-48">
        <ItemTitle className="flex-wrap">
          {row.label}
          <TemplateBadges row={row} />
        </ItemTitle>
        <ItemDescription>{row.description}</ItemDescription>
        {!row.available && (
          <p className="text-xs text-muted-foreground">{unavailableNote(row, context.terms)}</p>
        )}
      </ItemContent>
      <ItemActions>
        <Switch
          aria-label={`Send ${row.label}`}
          checked={row.template.enabled}
          disabled={!canEdit || pending}
          onCheckedChange={onEnabledChange}
        />
        <Button type="button" variant="outline" size="sm" onClick={onEdit}>
          {canEdit ? "Edit" : "View"}
        </Button>
      </ItemActions>
    </Item>
  );
}

/** The welcome message, set apart: a new shopper's first notification, often given a banner. */
function WelcomeCard({
  row,
  context,
  theme,
  now,
  ...actions
}: TemplateRowProps & { theme: StorefrontTheme; now: Date }) {
  const rendered = renderNotification(
    {
      kind: "system",
      type: row.type,
      data: notificationTypeDefinition(row.type).sample,
      template: row.template,
    },
    context,
  );
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center gap-2">
          Welcome message
          <TemplateBadges row={row} />
        </CardTitle>
        <CardDescription>
          {`${row.description} Give it a banner and a button to greet new shoppers the way a marketing notification would.`}
        </CardDescription>
        <CardAction>
          <Switch
            aria-label="Send the welcome message"
            checked={row.template.enabled}
            disabled={!actions.canEdit || actions.pending}
            onCheckedChange={actions.onEnabledChange}
          />
        </CardAction>
      </CardHeader>
      <CardContent className="grid items-start gap-4 md:grid-cols-[minmax(0,22rem)_auto]">
        <NotificationPreview category={row.category} rendered={rendered} theme={theme} now={now} />
        <div>
          <Button type="button" variant="outline" onClick={actions.onEdit}>
            {actions.canEdit ? "Edit welcome message" : "View welcome message"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
